/**
 * learnerModel.js — Cycle 56 · Part 4A
 * ────────────────────────────────────────────────────────────
 * Derives a per-child LEARNER MODEL from data we already collect:
 *   - mastery ledger (skills mastered, dates)
 *   - progress snapshot (streaks, lastPlayed, badges, coins)
 *   - question history (wrong answers, hint usage)
 *
 * No new tracking. Everything is computed from existing local storage
 * keys. Recomputed on demand — cheap; runs in <5ms for typical loads.
 *
 * Used by:
 *   - <AITeacher /> to feed context into `/api/teacher`
 *   - Parent "How [child] learns" summary
 *   - Companion greetings
 */
import { getActiveChild } from "@/lib/children";
import { getReadingLevel } from "@/lib/readingLevel";

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const parse = (raw, def) => { try { return JSON.parse(raw || "null") ?? def; } catch { return def; } };

/** Return the child's current progress snapshot (same shape used across app). */
export function progressSnapshot() {
  return parse(safeGet("we_progress_v1"), {});
}

/** Return the mastery ledger — { skillId: { state, times, lastAt } }. */
export function masteryLedger() {
  return parse(safeGet("we_mastery_v1"), {});
}

/** Recent activity log — synthesised from any signal we can find. */
function activityLog() {
  return parse(safeGet("we_activity_log_v1"), []);
}

/** Wrong answers list — used to detect error patterns.
 *  Each entry: { skill, chosen, correct, at, elapsedMs, hintUsed } */
function wrongAnswers() {
  return parse(safeGet("we_wrong_answers_v1"), []);
}

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

/** Median helper. */
function median(nums) {
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** Detect common error patterns from wrong answers (best effort). */
function detectErrorPatterns(wrongs) {
  if (!wrongs.length) return [];
  const patterns = [];
  const fastCount = wrongs.filter((w) => w.elapsedMs && w.elapsedMs < 2500).length;
  if (fastCount / wrongs.length > 0.5) patterns.push("guessing-fast");
  const guessLater = wrongs.filter((w) => w.attempt && w.attempt >= 3).length;
  if (guessLater > 2) patterns.push("guessing-on-repeat");
  const hintNoise = wrongs.filter((w) => w.hintUsed).length;
  if (hintNoise / wrongs.length > 0.6) patterns.push("hint-dependent");
  const carrying = wrongs.filter((w) => w.tag && String(w.tag).includes("carry")).length;
  if (carrying >= 2) patterns.push("carry-slip");
  const placeVal = wrongs.filter((w) => w.tag && String(w.tag).includes("place")).length;
  if (placeVal >= 2) patterns.push("place-value-slip");
  return patterns.slice(0, 3);
}

/** Preferred activity type from what they COMPLETE most. */
function preferredActivity(activity) {
  const counts = {};
  for (const a of activity) {
    if (!a?.kind || !a.completed) continue;
    counts[a.kind] = (counts[a.kind] || 0) + 1;
  }
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return entries[0]?.[0] || null;
}

/** Best time of day (morning / afternoon / evening) from session starts. */
function bestTimeOfDay(activity) {
  const buckets = { morning: 0, afternoon: 0, evening: 0 };
  for (const a of activity) {
    if (!a?.at) continue;
    const h = new Date(a.at).getHours();
    if (h < 12) buckets.morning++;
    else if (h < 17) buckets.afternoon++;
    else buckets.evening++;
  }
  const best = Object.entries(buckets).sort((a, b) => b[1] - a[1])[0];
  return best && best[1] > 0 ? best[0] : null;
}

/** Preferred delivery mode: video / worked / discover / practice-first. */
function preferredDelivery(activity) {
  const counts = { video: 0, worked_example: 0, discover: 0, practice_first: 0 };
  for (const a of activity) {
    if (!a?.mode || !a.completed) continue;
    if (counts[a.mode] != null) counts[a.mode]++;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best && best[1] > 0 ? best[0] : null;
}

/** Session length in minutes before drop-off (median across sessions). */
function sessionLengthMinutes(activity) {
  const sessions = activity.filter((a) => a.kind === "session-end" && typeof a.durationMs === "number");
  if (!sessions.length) return null;
  return Math.round(median(sessions.map((s) => s.durationMs / 60000)));
}

/** The main export — compute a summary object. */
export function computeLearnerModel() {
  const child = getActiveChild();
  const mastery = masteryLedger();
  const activity = activityLog();
  const wrongs = wrongAnswers();
  const progress = progressSnapshot();

  const mastered = Object.entries(mastery)
    .filter(([, v]) => v?.state === "mastered")
    .sort((a, b) => (b[1].lastAt || 0) - (a[1].lastAt || 0));
  const recentMastered = mastered.slice(0, 5).map(([id, v]) => ({ skillId: id, at: v.lastAt || null }));

  const struggling = Object.entries(mastery)
    .filter(([, v]) => v?.state === "attempted" && (v.times || 0) >= 3)
    .slice(0, 5)
    .map(([id]) => id);

  const daysSinceLast = progress.lastPlayed
    ? Math.floor((Date.now() - new Date(progress.lastPlayed).getTime()) / DAY)
    : null;

  const summary = {
    childName: child?.name || null,
    tier: child?.tier || "explorers",
    readingLevel: getReadingLevel(),
    aspiration: child?.aspiration || null,
    interests: child?.interests || [],

    // Journey
    totalMastered: mastered.length,
    recentMastered,
    struggling,
    streak: progress.streak || 0,
    daysSinceLast,
    stars: progress.stars || 0,
    badges: (progress.badges || []).length,

    // How they learn
    preferredActivity: preferredActivity(activity),
    preferredDelivery: preferredDelivery(activity),
    bestTimeOfDay: bestTimeOfDay(activity),
    typicalSessionMinutes: sessionLengthMinutes(activity),
    errorPatterns: detectErrorPatterns(wrongs),

    computedAt: new Date().toISOString(),
  };
  return summary;
}

/**
 * A shorter, prompt-friendly text version of the learner model
 * suitable for injection into the LLM context. Kept under ~500 chars.
 */
export function learnerModelPrompt() {
  const m = computeLearnerModel();
  if (!m || !m.childName) return "";
  const parts = [];
  parts.push(`Child: ${m.childName}, tier ${m.tier}, reading level ${m.readingLevel}.`);
  if (m.recentMastered.length) {
    const list = m.recentMastered.map((r) => r.skillId).slice(0, 3).join(", ");
    parts.push(`Recently mastered: ${list}.`);
  }
  if (m.struggling.length) parts.push(`Currently struggling with: ${m.struggling.slice(0, 2).join(", ")}.`);
  if (m.streak > 0) parts.push(`Current streak: ${m.streak} day${m.streak === 1 ? "" : "s"}.`);
  if (m.preferredDelivery) parts.push(`Learns best via: ${m.preferredDelivery.replace(/_/g, " ")}.`);
  if (m.errorPatterns.length) parts.push(`Error patterns: ${m.errorPatterns.join(", ")}.`);
  if (m.aspiration) parts.push(`Aspiration: ${m.aspiration}.`);
  if (m.interests?.length) parts.push(`Interests: ${m.interests.slice(0, 3).join(", ")}.`);
  return parts.join(" ").slice(0, 500);
}

/**
 * Record a wrong answer (called by quiz/duel/lesson pipelines).
 * Stored capped at 200 entries.
 */
export function recordWrongAnswer(entry) {
  try {
    const key = "we_wrong_answers_v1";
    const raw = safeGet(key) || "[]";
    const list = parse(raw, []);
    list.push({ ...entry, at: entry.at || Date.now() });
    localStorage.setItem(key, JSON.stringify(list.slice(-200)));
  } catch { /* ignore */ }
}

/** Record a completed activity (session, lesson, game). */
export function recordActivity(entry) {
  try {
    const key = "we_activity_log_v1";
    const raw = safeGet(key) || "[]";
    const list = parse(raw, []);
    list.push({ ...entry, at: entry.at || Date.now() });
    localStorage.setItem(key, JSON.stringify(list.slice(-200)));
  } catch { /* ignore */ }
}
